import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import type {
	AddressDTO,
	AddressLookupResponse,
	ConditionKey,
} from '@uturn/api/v1';
import {
	AdditionalRequirement,
	getPlaceDetailsByPlaceId,
	PriceCalculatorTransportType as TransportType,
} from '@uturn/api/v1';
import { usePriceCalculatorStore } from '@uturn/portal/store/price-calculator';
import { getLocationQueryString } from '@uturn/portal/utils';
import {
	AutoComplete,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormRadioGroupHorizontalItem,
	Icon,
	Label,
	RadioGroup,
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@uturn/ui-kit';
import getCountryISO3 from 'country-iso-3-to-2';
import { motion } from 'framer-motion';
import { useRef, useState } from 'react';
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
	type LocationField,
	type PriceRequestFormFields,
	priceRequestSchema,
} from '../schema';
import type { PriceRequestFormProps } from './price-request-form.types';
import {
	additionalRequirementOptions,
	containerSizeOptions,
	locationLabels,
	transportTypeOptions,
} from './price-request-form.types';

export type { LocationField, PriceRequestFormFields };

function addressLookupToAddressDTO(
	address: AddressLookupResponse,
): LocationField {
	return {
		id: address.id ?? undefined,
		name: address.name ?? null,
		...(address.placeId && { googlePlaceId: address.placeId }),
		street: address.street ?? null,
		city: address.city ?? null,
		postalCode: address.postalCode ?? '',
		countryCode: getCountryISO3(address.country?.countryCode ?? ''),
		latitude: address.latitude ?? null,
		longitude: address.longitude ?? null,
		houseNumber: address.houseNumber ?? null,
	};
}

function useAutoSuggestions() {
	const { fetchLocations } = usePriceCalculatorStore();
	const [autocompleteSuggestions, setAutocompleteSuggestions] = useState<
		string[]
	>([]);
	const [fullAutocompleteSuggestions, setFullAutocompleteSuggestions] =
		useState<AddressLookupResponse[]>([]);

	return {
		autocompleteSuggestions,
		getAutoCompleteSuggestions: (text: string) => {
			fetchLocations({ searchText: text }).then((locations) => {
				setAutocompleteSuggestions(
					locations.map(
						(item) =>
							`${item.name ?? ''} ${item.street ?? ''} ${item.city ?? ''}`,
					),
				);

				setFullAutocompleteSuggestions(locations);
			});
		},
		fullAutocompleteSuggestions,
	};
}

function AdditionalRequirementsRadioGroup({
	additionalRequirementEnabledFeatures = [],
}: {
	additionalRequirementEnabledFeatures?: ConditionKey[];
}) {
	const { t } = useTranslation();
	const form = useFormContext<PriceRequestFormFields>();

	if (!additionalRequirementEnabledFeatures.length) {
		return (
			<div>
				<Label>
					{t(
						'pages.price-calculator.form.fields.additional-requirements.label',
						'Additional requirements',
					)}
				</Label>
				<div className="relative">
					<div className="text-secondary-600 absolute z-10 grid size-full items-center justify-center text-center font-bold">
						{t(
							'pages.price-calculator.form.fields.additional-requirements.no-access',
							'To access additional requirements, please upgrade your subscription.',
						)}
					</div>
					<div className="blur-md">
						<FormItem>
							<FormControl>
								<RadioGroup
									defaultValue={AdditionalRequirement.NONE}
									disabled
									className="mt-2 flex justify-between"
								>
									{additionalRequirementOptions(t).map((item) => (
										<FormRadioGroupHorizontalItem
											key={item.value}
											id={item.value}
											value={item.value}
											label={item.label}
											labelPosition="left"
										/>
									))}
								</RadioGroup>
							</FormControl>
						</FormItem>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div>
			<Label className="mt-4 flex items-center gap-2">
				{t(
					'pages.price-calculator.form.fields.additional-requirements.label',
					'Additional requirements',
				)}
				<TooltipProvider delayDuration={10}>
					<Tooltip>
						<TooltipTrigger>
							<Icon icon={faInfoCircle} className="size-4" />
						</TooltipTrigger>
						<TooltipContent>
							{t(
								'pages.price-calculator.form.fields.additional-requirements.tooltip',
								'Additional transport requirements that can be added to the price request.',
							)}
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			</Label>
			<FormField
				name="additionalRequirement"
				control={form.control}
				render={({ field: { onChange, value } }) => (
					<FormItem>
						<FormControl>
							<RadioGroup
								onValueChange={onChange}
								value={value}
								className="mt-2 flex justify-between"
							>
								{additionalRequirementOptions(t)
									.filter(
										(item) =>
											!item.conditionKey ||
											additionalRequirementEnabledFeatures.includes(
												item.conditionKey,
											),
									)
									.map((item) => (
										<FormRadioGroupHorizontalItem
											key={item.value}
											id={item.value}
											value={item.value}
											label={item.label}
											labelPosition="left"
										/>
									))}
							</RadioGroup>
						</FormControl>
					</FormItem>
				)}
			/>
		</div>
	);
}

export function PriceRequestForm({
	defaultValues,
	onSubmit,
	onTripChange,
	additionalRequirementEnabledFeatures,
}: PriceRequestFormProps) {
	const { t } = useTranslation();
	const {
		autocompleteSuggestions,
		getAutoCompleteSuggestions,
		fullAutocompleteSuggestions,
	} = useAutoSuggestions();

	const suggestions = autocompleteSuggestions.map((suggestion) => ({
		label: suggestion,
		value: suggestion,
	}));

	const form = useForm<PriceRequestFormFields>({
		resolver: zodResolver(priceRequestSchema),
		mode: 'onChange',
		defaultValues,
	});

	const { fetchRouteInformation } = usePriceCalculatorStore();

	const watchLocations = form.watch('locations');
	const watchTransportType = form.watch('transportType');

	const { fields, append, remove } = useFieldArray({
		control: form.control,
		name: 'locations',
		rules: {
			required: t(
				'general.field-required',
				'This field is required',
			).toString(),
		},
	});

	// SHUNT ships between two locations; every other transport type has three.
	// Adjusting the field array here (on change) rather than in an effect keeps
	// the mutation tied to the user action that causes it.
	const handleTransportTypeChange = (nextTransportType: string) => {
		if (nextTransportType === TransportType.SHUNT) {
			if (fields.length > 2) {
				remove(2);
			}
		} else if (fields.length < 3) {
			append({});
		}
	};

	const formRef = useRef<HTMLFormElement>(null);

	// // TODO Remove any's and fix this
	async function onSelect(indexLocation: number, indexAutoSuggestion: number) {
		const location = fullAutocompleteSuggestions[indexAutoSuggestion];

		if (location.source?.toUpperCase() === 'GOOGLE') {
			const res = await getPlaceDetailsByPlaceId({
				placeId: location.placeId ?? '',
			});

			form.setValue(
				`locations.${indexLocation}`,
				addressLookupToAddressDTO(res.data),
				{ shouldValidate: true },
			);
		} else {
			form.setValue(
				`locations.${indexLocation}`,
				addressLookupToAddressDTO(location),
				{ shouldValidate: true },
			);
		}

		const requiredLocationsLength =
			watchTransportType === TransportType.SHUNT ? 2 : 3;
		const locations: AddressDTO[] = structuredClone(watchLocations)
			.filter(
				(location) =>
					location !== undefined && Object.keys(location).length !== 0,
			)
			.slice(0, requiredLocationsLength) as AddressDTO[];

		if (locations.length === requiredLocationsLength) {
			fetchRouteInformation({
				locations,
				transportType: watchTransportType,
			});
		}

		onTripChange(locations);

		return null;
	}

	function locationTypeLabel(i: number): string {
		return t('components.shipment_location.type', '{{locationType}} location', {
			locationType: locationLabels(t)[watchTransportType][i],
		});
	}

	return (
		<motion.div
			initial={{ opacity: 0, translateY: 15 }}
			animate={{ opacity: 1, translateY: 0 }}
			exit={{ opacity: 0, translateY: 15 }}
			transition={{ duration: 0.3, ease: 'easeInOut' }}
		>
			<Card key="unlocked">
				<CardHeader>
					<CardTitle>
						{t('pages.price-calculator.form.title', 'Request a price')}
					</CardTitle>
					<CardDescription>
						{t(
							'pages.price-calculator.form.description',
							'Market price insight with the UTURN price calculator. Provide your shipment details and get objective data based insights in price.',
						)}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<Form {...form}>
						<form
							className="flex flex-col gap-3"
							ref={formRef}
							onSubmit={form.handleSubmit(onSubmit)}
						>
							<FormField
								name="transportType"
								control={form.control}
								render={({ field: { onChange, value } }) => (
									<FormItem>
										<FormControl>
											<RadioGroup
												onValueChange={(nextTransportType) => {
													onChange(nextTransportType);
													handleTransportTypeChange(nextTransportType);
												}}
												value={value}
												className="mt-2 flex justify-between"
											>
												{transportTypeOptions(t).map((item) => (
													<FormRadioGroupHorizontalItem
														key={item.value}
														id={item.value}
														value={item.value}
														label={item.label}
														labelPosition="left"
													/>
												))}
											</RadioGroup>
										</FormControl>
									</FormItem>
								)}
							/>
							<div className="my-4 flex flex-col gap-4">
								{fields.map((item, index) => (
									<FormField
										key={item.id}
										name={`locations.${index}`}
										control={form.control}
										render={({ field }) => (
											<FormItem>
												<FormLabel>{locationTypeLabel(index)}</FormLabel>
												<AutoComplete
													refCallback={field.ref}
													value={getLocationQueryString(field.value)}
													options={suggestions}
													emptyMessage={t(
														'pages.price-calculator.form.fields.location.dropdown.no-result',
														'No location found',
													)}
													debouncerDelay={1000}
													onInputChange={(e) => {
														if (e === '') {
															field.onChange({});
															return;
														}
														getAutoCompleteSuggestions(e);
													}}
													setSelected={(selectIndex) =>
														onSelect(index, selectIndex)
													}
													forceUniqueLabelsEnabled={true}
													placeholder={t(
														'pages.price-calculator.form.fields.location.dropdown.placeholder',
														'Search and select {{ location }} in Europe',
														{
															location: locationTypeLabel(index).toLowerCase(),
														},
													).toString()}
												/>
												<FormMessage />
											</FormItem>
										)}
									/>
								))}
							</div>
							<FormField
								name="containerSize"
								control={form.control}
								render={({ field: { onChange, value } }) => (
									<FormItem>
										<FormControl>
											<RadioGroup
												onValueChange={onChange}
												value={value}
												className="mt-2 flex justify-between"
											>
												{containerSizeOptions(t).map((item) => (
													<FormRadioGroupHorizontalItem
														key={item.value}
														id={item.value}
														value={item.value}
														label={item.label}
														labelPosition="left"
													/>
												))}
											</RadioGroup>
										</FormControl>
									</FormItem>
								)}
							/>
							<AdditionalRequirementsRadioGroup
								additionalRequirementEnabledFeatures={
									additionalRequirementEnabledFeatures
								}
							/>
							<div className="mt-8 flex flex-row justify-end">
								<Button disabled={!form.formState.isValid} type="submit">
									{t('pages.price-calculator.request-price', 'Request price')}
								</Button>
							</div>
						</form>
					</Form>
				</CardContent>
			</Card>
		</motion.div>
	);
}
